import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Box, Typography, Divider, Grid2, Checkbox, Tooltip } from "@mui/material";
import SearchInput from "../../Overview/components/SearchInput";
import {
  GroupByButton,
  DropdownMenu,
  GroupByMenuItem,
} from "../../BmsFaults/Components/StyledComponents";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { useFleetBreakdownData } from "../../FleetPerformance/Hooks/useFleetBreakdownData";
import { getSOHBreakdownGroupByData, getSOHBreakdownChartData } from "../../../Api/BatteryHealthApi";
import SOHBreakdownBarChart from "../../../Charts/Bar/SOHBreakdownBarChart";
import SOHBreakdownDrillDownModal from "./SOHBreakdown/SOHBreakdownDrillDownModal";
import { useBaseEntityInfo, useOrgLabels } from "../../../Hooks/usePagesContent";
import { applyDimensionLabels } from "../../../Helper/Analytics/breakdownDimensions";
import { useSOHBuckets, buildSOHBuckets } from "./utils/sohBuckets";

// ─── Constants ─────────────────────────────────────────────────────────────────
const GROUP_BY_OPTIONS = [
  { value: "originCity",  label: "By City",               apiKey: "originCity"  },
  { value: "originState", label: "By State",              apiKey: "originState" },
  { value: "originDepot", label: "By Depot",              apiKey: "originDepo"  },
  { value: "mfgYear",     label: "By Mfg. Year",          apiKey: "mfgYear"     },
  { value: "configName",  label: "By Configuration",      apiKey: "configName"  },
  // The two customer dimensions are renamed at render time from globalConfig.labels.
  { value: "fleetName",     label: "By Customer Account", apiKey: "fleetName"     },
  { value: "fleetCategory", label: "By Business Segment", apiKey: "fleetCategory" },
];

// Fast value → apiKey lookup
const GROUP_BY_API_KEY = Object.fromEntries(
  GROUP_BY_OPTIONS.map((o) => [o.value, o.apiKey])
);

const AXIS_Y_TO_GROUP_BY = {
  city:              { value: "originCity",    label: "By City",               apiKey: "originCity"    },
  state:             { value: "originState",   label: "By State",              apiKey: "originState"   },
  depot:             { value: "originDepot",   label: "By Depot",              apiKey: "originDepo"    },
  manufacturingYear: { value: "mfgYear",       label: "By Manufacturing Year", shortLabel: "By Mfg. Year", apiKey: "mfgYear" },
  configName:        { value: "configName",    label: "By Configuration",      apiKey: "configName"    },
  project:           { value: "project",       label: "By Project",            apiKey: "configName"    },
  cellChemistry:     { value: "cellChemistry", label: "By Cell Chemistry",     apiKey: "cellChemistry" },
  fleetName:         { value: "fleetName",     label: "By Customer Account",   apiKey: "fleetName"     },
  fleetCategory:     { value: "fleetCategory", label: "By Business Segment",   apiKey: "fleetCategory" },
};

const SIDEBAR_LIST_MAX_HEIGHT = "350px";

// SOH bucket order fallback (used only if sohBuckets hook not yet loaded).
// "SOH Unavailable" (no SOH) is always last.
const SOH_BUCKET_ORDER_FALLBACK = buildSOHBuckets(undefined).map((b) => b.name);
// ─── Component ─────────────────────────────────────────────────────────────────
const StateOfHealthBreakdownChart = ({ globalFilters, chartConfig = {} }) => {
  const { baseEntityTypePluralLable } = useBaseEntityInfo();
  const { fleetLabel, fleetCategoryLabel } = useOrgLabels();
  // Includes "SOH Unavailable" last: the backend counts every in-scope battery, so the
  // bar percentages and the sidebar "All" count cover the same population.
  const sohBuckets       = useSOHBuckets();
  const sohBucketOrder   = useMemo(() => sohBuckets.map((b) => b.name), [sohBuckets]);
  const sohDisplayLabels = useMemo(
    () => Object.fromEntries(sohBuckets.map((b) => [b.name, b.displayLabel ?? b.name])),
    [sohBuckets]
  );
  const sohLabelColorMap = useMemo(
    () => Object.fromEntries(sohBuckets.map((b) => [b.name, b.color])),
    [sohBuckets]
  );

  const groupByOptions = useMemo(() => {
    const combinations = chartConfig?.sohBreakdownChart?.axisCombinations || [];
    const options = combinations.length === 0
      ? GROUP_BY_OPTIONS
      : combinations.map((combo) => AXIS_Y_TO_GROUP_BY[combo.y]).filter(Boolean);
    // The customer dimensions take their names from the same org meta row that names
    // them everywhere else, so renaming the customer renames these menu entries too.
    return applyDimensionLabels(options, {
      fleetName: `By ${fleetLabel}`,
      fleetCategory: `By ${fleetCategoryLabel}`,
    });
  }, [chartConfig, fleetLabel, fleetCategoryLabel]);

  const groupByApiKey = useMemo(() =>
    Object.fromEntries(groupByOptions.map((o) => [o.value, o.apiKey])),
    [groupByOptions]
  );

  // Honor axisCombinations[].isDefault for the initial group-by; fall back to the first option.
  const defaultGroupBy = useMemo(() => {
    const combinations = chartConfig?.sohBreakdownChart?.axisCombinations || [];
    const preferred = combinations.find((c) => c.isDefault);
    return AXIS_Y_TO_GROUP_BY[preferred?.y]?.value ?? groupByOptions[0]?.value ?? "originCity";
  }, [chartConfig, groupByOptions]);

  const [groupBy, setGroupBy] = useState(defaultGroupBy);
  const [searchQuery, setSearchQuery]           = useState("");
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);

  const [modalOpen, setModalOpen]                 = useState(false);
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);

  // ─── Sidebar API fetch ──────────────────────────────────────────────────────
  const handleFetchSidebarData = useCallback(async (currentGroupBy) => {
    const apiKey = groupByApiKey[currentGroupBy] || groupByOptions[0]?.apiKey || "originCity";
    try {
      const response = await getSOHBreakdownGroupByData(globalFilters, apiKey);
      if (response?.responseStatus?.code === 200) {
        const data  = response.response?.data || [];
        const items = data.map((item) => ({
          id:    item.xLabel,
          label: item.xLabel,
          count: item.metricsArray?.find((m) => m.type === "DistinctVehicleCount")?.value ?? 0,
        }));
        if (items.length > 0) {
          const totalCount = items.reduce((s, i) => s + i.count, 0);
          return { items: [{ id: "all", label: "All", count: totalCount }, ...items] };
        }
      }
    } catch (err) {
      console.error("SOHBreakdown: sidebar fetch error", err);
    }
    return { items: [] };
  }, [globalFilters, groupByApiKey, groupByOptions]);

  // ─── Chart data fetch ───────────────────────────────────────────────────────
  const handleFetchChartData = useCallback(async (currentGroupBy, selectedItems) => {
    const apiKey = groupByApiKey[currentGroupBy] || groupByOptions[0]?.apiKey || "originCity";
    const isAllSelected = selectedItems.some((i) => i.id === "all");
    const filterValues  = isAllSelected ? [] : selectedItems.map((i) => i.id);

    const response = await getSOHBreakdownChartData(globalFilters, apiKey, filterValues);
    if (response?.responseStatus?.code !== 200) return [];

    const data    = response.response?.data ?? [];
    const countMap = Object.fromEntries(data.map(({ xLabel, metricsArray }) => [
      xLabel,
      metricsArray?.find((m) => m.type === "DistinctVehicleCount")?.value ?? 0,
    ]));

    const total = Object.values(countMap).reduce((s, v) => s + v, 0);

    const bucketOrder = sohBucketOrder.length > 0 ? sohBucketOrder : SOH_BUCKET_ORDER_FALLBACK;
    return bucketOrder.map((bucket) => {
      const count = countMap[bucket] ?? 0;
      return {
        category:     bucket,                             // wire label (drill-down filter)
        displayLabel: sohDisplayLabels[bucket] ?? bucket, // UI label
        count,
        percentage: total > 0 ? parseFloat(((count / total) * 100).toFixed(2)) : 0,
      };
    });
  }, [globalFilters, groupByApiKey, groupByOptions, sohBucketOrder, sohDisplayLabels]);

  const config = useMemo(() => ({
    onFetchSidebarData: handleFetchSidebarData,
    onFetchChartData:   handleFetchChartData,
  }), [handleFetchSidebarData, handleFetchChartData]);

  const {
    groupByItems,
    selectedItems,
    setSelectedItems,
    chartData,
    loadingGroupBy,
    loadingChart,
  } = useFleetBreakdownData({ dependencies: [globalFilters], groupBy, config });

  const chartRows = chartData;

  // Keep a ref so the click handler always reads fresh state
  const clickStateRef = useRef({ selectedItems });
  useEffect(() => { clickStateRef.current = { selectedItems }; }, [selectedItems]);

  const handleGroupByChange = (value) => {
    setGroupBy(value);
    setSearchQuery("");
    setDropdownAnchorEl(null);
  };

  const handleItemToggle = useCallback((item) => {
    if (loadingChart) return;
    setSelectedItems((prev) => {
      const isAllSelected = prev.some((i) => i.id === "all");
      let next = [];
      if (item.id === "all") {
        next = isAllSelected ? [] : [item];
      } else {
        if (isAllSelected) {
          next = groupByItems.filter((i) => i.id !== "all" && i.id !== item.id);
        } else {
          next = prev.some((i) => i.id === item.id)
            ? prev.filter((i) => i.id !== item.id)
            : [...prev, item];
        }
      }
      const allIndividual = groupByItems.filter((i) => i.id !== "all");
      if (next.length === allIndividual.length && !next.some((i) => i.id === "all")) {
        const allItem = groupByItems.find((i) => i.id === "all");
        if (allItem) return [allItem];
      }
      return next;
    });
  }, [groupByItems, loadingChart, setSelectedItems]);

  const filteredItems = groupByItems.filter((item) =>
    item.label?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ─── Bar click → open drill-down modal ─────────────────────────────────────
  const handleBarClick = useCallback((row) => {
    const { selectedItems: current } = clickStateRef.current;
    const isAllSelected = current.some((i) => i.id === "all");
    const locationLabel = isAllSelected
      ? "All"
      : current.map((i) => i.label).join(", ");
    // Carry the active left-side filter into the drilldown API call
    const locationFilter = isAllSelected
      ? null
      : { name: groupByApiKey[groupBy], values: current.map((i) => i.id) };
    setSelectedDataPoint({
      sohCategory:      row.category,
      sohCategoryLabel: row.displayLabel ?? row.category,
      locationLabel,
      locationFilter,
    });
    setModalOpen(true);
  }, [groupBy, groupByApiKey]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedDataPoint(null);
  }, []);

  const selectedOption = groupByOptions.find((o) => o.value === groupBy);

  return (
    <>
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          maxWidth: "calc(100vw - 102px)",
        }}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "28px",
            py: "24px",
          }}
        >
          <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
            State of Health Breakdown
          </Typography>
        </Box>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", overflowX: "auto" }}>

          {/* ── Left sidebar ──────────────────────────────────────────────── */}
          <Box
            sx={{
              width: "330px",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              mb: 2,
              padding: "0px 0px 30px 0px"
            }}
          >
            <Box sx={{ padding: "0 16px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Tooltip
                  title={selectedOption?.shortLabel ? selectedOption.label : ""}
                  placement="top"
                  arrow
                >
                  <GroupByButton
                    onClick={(e) => setDropdownAnchorEl(e.currentTarget)}
                    endIcon={<ArrowDropDownIcon sx={{ color: "#5E758D", fontSize: "10px" }} />}
                    startIcon={<GridViewRoundedIcon sx={{ color: "#5E758D", fontSize: "12px" }} />}
                  >
                    {selectedOption?.shortLabel ?? selectedOption?.label}
                  </GroupByButton>
                </Tooltip>
                <Typography variant="fw400s12c686868">NO. OF {baseEntityTypePluralLable.toUpperCase()}</Typography>
              </Box>

              <Divider sx={{ mb: 1, borderColor: "#E4E4E4" }} />

              <Box sx={{ mb: 1 }}>
                <SearchInput
                  placeholder={`Search ${(selectedOption?.shortLabel ?? selectedOption?.label)?.replace("By ", "") || "City"}`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Box>

              <DropdownMenu
                anchorEl={dropdownAnchorEl}
                open={Boolean(dropdownAnchorEl)}
                onClose={() => setDropdownAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                {groupByOptions.map((option) => (
                  <GroupByMenuItem
                    key={option.value}
                    selected={groupBy === option.value}
                    onClick={() => handleGroupByChange(option.value)}
                  >
                    {option.label}
                  </GroupByMenuItem>
                ))}
              </DropdownMenu>
            </Box>

            <Box sx={{ maxHeight: SIDEBAR_LIST_MAX_HEIGHT, overflowY: "auto", padding: "0px 20px 0px 28px" }}>
              {loadingGroupBy ? (
                <Box sx={{ padding: "16px", display: "flex", justifyContent: "center" }}>
                  <CartLoader />
                </Box>
              ) : filteredItems.length === 0 ? (
                <Box sx={{ padding: "16px", textAlign: "center" }}>
                  <Typography variant="fw400s14c686868">No items found</Typography>
                </Box>
              ) : (
                <Grid2 container gap={0.5} flexDirection="column">
                  {filteredItems.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => handleItemToggle(item)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 0",
                        cursor: "pointer",
                        "&:hover": { opacity: 0.8 },
                        transition: "opacity 0.2s",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Checkbox
                          checked={
                            selectedItems.some((i) => i.id === "all") ||
                            selectedItems.some((i) => i.id === item.id)
                          }
                          indeterminate={
                            item.id === "all" &&
                            !selectedItems.some((i) => i.id === "all") &&
                            selectedItems.length > 0
                          }
                          sx={{
                            padding: 0,
                            color: "#6D72F6",
                            "&.Mui-checked":               { color: "#6D72F6" },
                            "&.MuiCheckbox-indeterminate": { color: "#6D72F6" },
                            "& .MuiSvgIcon-root":          { fontSize: "20px" },
                          }}
                        />
                        <Typography
                          variant="fw400s14c000000"
                          sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="fw600s14c000000"
                        sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}
                      >
                        {item.count}
                      </Typography>
                    </Box>
                  ))}
                </Grid2>
              )}
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", mb: 6 }} />

          {/* ── Right chart section ──────────────────────────────────────── */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              padding: "24px 28px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {loadingChart ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 320 }}>
                <CartLoader />
              </Box>
            ) : selectedItems.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 320 }}>
                <Typography variant="fw400s14c686868">Select at least one item</Typography>
              </Box>
            ) : (
              <SOHBreakdownBarChart
                chartRows={chartRows}
                loading={false}
                onBarClick={handleBarClick}
                labelColorMap={sohLabelColorMap}
                yAxisLabel="SOH (%)"
              />
            )}
          </Box>

        </Box>
      </Box>

      {/* ── Drill-down modal ────────────────────────────────────────────────── */}
      <SOHBreakdownDrillDownModal
        open={modalOpen}
        onClose={handleModalClose}
        selectedDataPoint={selectedDataPoint}
        globalFilters={globalFilters}
      />
    </>
  );
};

export default StateOfHealthBreakdownChart;
